"use client";

import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import {
  optimizeProductImageUrl,
  optimizeProductImageUrlFillVertical,
} from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import { observer } from "mobx-react";
import React, { Suspense, useRef } from "react";
import styled, { css } from "styled-components";

import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { selectBadge } from "@faire/retailer/components/ProductBadge/badgeUtils";
import { QuickAdd, QuickAddButton } from "@faire/retailer/components/QuickAdd";
import { RoundQuantityPickerWrapper } from "@faire/retailer/components/QuickAdd/EncapsulatedQuickAdd";
import { LazyEncapsulatedQuickAdd } from "@faire/retailer/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { LazyDebugInfoSection } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/DebugInfo/LazyDebugInfoSection";
import { TilePriceBadge } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/TilePriceBadge";
import {
  ControlsOverlay,
  TrackedProductLink,
} from "@faire/retailer/features/MarketplaceItems/styled";
import { TopBadges } from "@faire/retailer/features/MarketplaceItems/TopBadges";
import { SaveToBoard } from "@faire/retailer/features/RetailerBoards/Components/SaveToBoard";
import { useCartItems } from "@faire/retailer/hooks/cart/useCartItems";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer/lib/verticalAspectRatio";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

import { ProductImageWithSrcsetSizes } from "../ProductImageWithSrcsetSizes";

const ProductImageSectionBase: React.FC<ProductImageSection.IProps> = (
  props
) => {
  const {
    productTile,
    brand,
    hideCartQuantity,
    generateProductLinkProps,
    altTag,
    children,
    onTopLeftBadgeVisible,
    onQuickAdd,
    onQuickAddSubmit,
    onChooseOption,
    onHeartClicked,
    onSaveToBoard,
    onRemoveFromBoard,
    onChooseOptionQuantityUpdate,
    showPriceBadge,
    showVerticalImagery,
    disableQuickAddWithFaireProtectionTooltip,
    hideAddToCart,
    useLazyImageLoading,
    imageBreakpoints,
  } = props;

  const userStore = useUserStore(["isEmployee", "isImpersonating"]);

  const quickAddButtonBottomOffset = "10px";

  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  const topBadge = selectBadge(productTile?.badge_list?.badges ?? [], "TOP");

  const { getProductTileQuantityInCart } = useCartItems();

  const cartQuantity = getProductTileQuantityInCart(
    productTile?.product?.token ?? ""
  );

  const productLinkProps = productTile?.product?.token
    ? generateProductLinkProps?.(productTile.product.token)
    : undefined;

  const image = productTile?.best_image;
  const altText = altTag ?? image?.alt_text;

  const onProductClick = (event: React.MouseEvent<HTMLElement>) => {
    if (productTile) {
      props.onProductClick?.(productTile, event);
    }
  };

  const renderDebugInfoSection = () => {
    if (!productTile?.product?.token) {
      return null;
    }
    if (!(userStore.isEmployee || userStore.isImpersonating)) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <LazyDebugInfoSection
          productToken={productTile.product.token}
          promotedBy={productTile.product.promoted_by}
          debugInfo={productTile.retrieval_and_ranking_debug_info}
        />
      </Suspense>
    );
  };

  const renderOverlayContent = () => {
    return (
      <>
        <ControlsOverlay>
          {renderExtraContent()}
          {children}
          {renderDebugInfoSection()}
        </ControlsOverlay>
        <TransparencyOverlay />
      </>
    );
  };

  const renderExtraContent = () => {
    if (!productTile?.product) {
      return null;
    }

    const extraElements = [];

    const showAddToCart = canAddToCart(brand) && !hideAddToCart;

    extraElements.push(
      <TopBadges
        key="top-badges"
        tileBadge={topBadge}
        favoriteButton={
          isLoggedInRetailer() ? (
            <StyledSaveToBoard
              productToken={productTile.product.token}
              productImage={productTile.best_image}
              boardTokens={productTile.product.board_tokens}
              brandToken={productTile.product.brand_token}
              onHeartClicked={onHeartClicked}
              onSaveToBoard={onSaveToBoard}
              onRemoveFromBoard={onRemoveFromBoard}
            />
          ) : null
        }
        showEventBadge={
          productTile?.is_highlighted_product_for_promotional_event
        }
        productToken={productTile?.product?.token}
        onTopLeftBadgeVisible={onTopLeftBadgeVisible}
      />
    );

    if (showAddToCart && productTile?.quick_add) {
      if (props.showCollapsibleQuickAddButton) {
        extraElements.push(
          <Suspense
            fallback={null}
            key={`${productTile.product?.token}-suspense`}
          >
            <LazyEncapsulatedQuickAdd
              key={`${productTile.product?.token}-encapsulated-quick-add`}
              productToken={productTile.product?.token}
              quickAdd={productTile.quick_add}
              brandToken={productTile.product?.brand_token}
              imageContainerRef={imageContainerRef}
              onQuickAddClick={onQuickAdd}
              onQuickAddSubmit={onQuickAddSubmit}
              onChooseOption={onChooseOption}
              onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
              disableQuickAddWithFaireProtectionTooltip={
                disableQuickAddWithFaireProtectionTooltip
              }
            />
          </Suspense>
        );
      } else if (!disableQuickAddWithFaireProtectionTooltip) {
        extraElements.push(
          <QuickAdd
            key={`${productTile.product.token}-quick-add`}
            productToken={productTile.product.token!}
            brandToken={productTile.product.brand_token!}
            quickAddData={productTile.quick_add}
            onQuickAddOption={onQuickAdd}
            onChooseOption={onChooseOption}
            forceShowModal={!!productTile.product.per_style_min_order_quantity}
            style={{ bottom: quickAddButtonBottomOffset }}
            isHighlightedProductForPromotionalEvent={
              productTile.is_highlighted_product_for_promotional_event
            }
          />
        );
      }
    } else if (showPriceBadge) {
      extraElements.push(
        <TilePriceBadge
          key={`${productTile.product.token}-price-badge`}
          productTile={productTile}
        />
      );
    }

    return extraElements.length > 0 ? extraElements : null;
  };

  return (
    <TrackedProductLink
      token={productTile?.product?.token}
      onClick={onProductClick}
      to={productLinkProps?.productUrl ?? ""}
      data-test-id="product-image-section"
      replace={productLinkProps?.replace}
    >
      <ProductOverlay
        ref={imageContainerRef}
        $showQuantity={!hideCartQuantity && (cartQuantity ?? 0) > 0}
      >
        <MarketplaceImage
          image={image}
          alt={altText}
          showVerticalImagery={showVerticalImagery}
          useLazyImageLoading={useLazyImageLoading}
          imageBreakpoints={imageBreakpoints}
        />
        {renderOverlayContent()}
      </ProductOverlay>
    </TrackedProductLink>
  );
};

export const ProductImageSection = observer(ProductImageSectionBase);
export namespace ProductImageSection {
  export interface IProps {
    children?: React.ReactNode | undefined;
    brand: ILeanProductTile.IBrand | undefined;
    productTile: ILeanProductTile | undefined;
    hideCartQuantity?: boolean;
    onQuickAdd?: (newQuantity?: number) => void;
    onQuickAddSubmit?: (newQuantity?: number) => void;
    onChooseOption?: () => void;
    onHeartClicked?: () => void;
    onSaveToBoard?: (boardToken?: string) => void;
    onRemoveFromBoard?: (boardToken?: string) => void;
    onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;
    generateProductLinkProps?: (productToken: string) =>
      | {
          productUrl: string;
          replace?: boolean;
        }
      | undefined;
    onProductClick?: (
      productTile: ILeanProductTile,
      event: React.MouseEvent<HTMLElement>
    ) => void;
    showVerticalImagery?: boolean;
    // Used by f-homepage-top-of-fold-recs for the new product image tile
    showPriceBadge?: boolean;
    onTopLeftBadgeVisible?: (tileBadge?: IBadge) => void;
    altTag?: string | undefined;
    showCollapsibleQuickAddButton?: boolean;
    disableQuickAddWithFaireProtectionTooltip?: boolean;
    hideAddToCart?: boolean;
    useLazyImageLoading?: boolean;
    imageBreakpoints?: string;
  }
}

const MarketplaceImageBase: React.FC<{
  className?: string;
  image?: IImage;
  alt?: string | undefined;
  showVerticalImagery?: boolean;
  useLazyImageLoading?: boolean;
  imageBreakpoints?: string;
}> = ({
  className,
  image,
  alt,
  showVerticalImagery,
  useLazyImageLoading,
  imageBreakpoints,
}) => {
  const responsiveWidth = useResponsiveValue({
    mobileAndAbove: 360,
    tabletAndAbove: 250,
    xLargeAndAbove: 300,
  });

  const isSSR = useIsSSR();

  const dataTestId = `product-image${showVerticalImagery ? "-vertical" : ""}`;

  if (image && imageBreakpoints) {
    return (
      <ProductImageWithSrcsetSizes
        image={image}
        alt={alt ?? image.alt_text ?? ""}
        loading={useLazyImageLoading ? "lazy" : undefined}
        sizes={imageBreakpoints}
        showVerticalImagery={showVerticalImagery}
        addOverlay={false}
      />
    );
  }

  return (
    <AspectRatioPicture
      className={className}
      $width="100%"
      $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
      $hideBorder
      data-test-id={dataTestId}
    >
      <img
        role="presentation"
        alt={alt}
        loading={useLazyImageLoading ? "lazy" : undefined}
        src={
          // eslint-disable-next-line no-nested-ternary
          image
            ? showVerticalImagery
              ? optimizeProductImageUrlFillVertical(
                  image,
                  isSSR,
                  responsiveWidth
                )
              : optimizeProductImageUrl(image, isSSR, responsiveWidth)
            : EAEAEA_PIXEL
        }
      />
    </AspectRatioPicture>
  );
};

const MarketplaceImage = observer(MarketplaceImageBase);

const StyledSaveToBoard = styled(SaveToBoard)`
  position: relative;
`;

const ProductOverlay = styled.div<{
  $showQuantity?: boolean;
}>`
  position: relative;
  display: flex;

  ${QuickAddButton} {
    display: none;
  }
  ${RoundQuantityPickerWrapper} {
    ${(props) =>
      !props.$showQuantity
        ? css`
            display: none;
          `
        : ""}
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    &:hover {
      ${QuickAddButton} {
        display: flex;
      }
      ${RoundQuantityPickerWrapper} {
        display: flex;
      }
    }
  }
`;
